import TransactionStatusBadge from '@/app/common/components/TransactionStatusBadge'
import { CircleArrowsIcon } from '@/app/common/icons/arrows'
import { displayTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { UTCToUsersTimezone } from '@/app/common/utils/dates/dates.utils'
import { Button } from '@/common/components/Button.tsx'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { TransactionListItem } from '@/schemas/transactions.schema'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectSelectedStudent } from '@/storage/applications/familyApplicationSlice'

const columnHelper = createColumnHelper<TransactionListItem>()

interface Props {
  transactions: TransactionListItem[]
  currentPage: number
  onPageChange: (page: number) => void
  recordsPerPage: number
  onPerPageChange: (perPage: number) => void
  search: string
  onSearchChange: (search: string) => void
  total_rows: number
  sortBy: string
  sortOrder: string
  onSortChange: (sortBy: string, sortOrder: string) => void
  onSortByChange: (sortBy: string) => void
}

export default function TransactionsTable(props: Props) {
  const {
    transactions,
    search,
    recordsPerPage,
    currentPage,
    onPageChange,
    onPerPageChange,
    onSearchChange,
    total_rows,
  } = props
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const student = useSelector(selectSelectedStudent)

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const handlePerPageChange = (perPage: number) => {
    onPerPageChange(perPage)
  }

  const columns = [
    columnHelper.accessor('transaction_id', {
      id: 'transaction_no',
      header: () => (
        <>
          <span className="hidden pl-4 sm:block">{translate('ID')}</span>
          <span className="block pl-4 sm:hidden">{translate('ID')}</span>
        </>
      ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.transaction_id}</span>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{row.student_name}</div>
                <div>{row.type}</div>
                <div>{row.provider_name}</div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor((value) => value.student_name.join(', '), {
      id: 'student_name',
      header: () => <>{translate('student_name')}</>,
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('transaction_type', {
      id: 'transaction_type',
      header: () => <>{translate('type')}</>,
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('provider_name', {
      id: 'provider',
      header: () => <>{translate('provider')}</>,
      cell: (info) => (
        <div className="max-w-[6.5rem] truncate">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('invoice_quote', {
      id: 'invoice_quote',
      header: () => 'Invoice No.',
      cell: (info) => (
        <div className="max-w-[5rem] truncate">
          {info.getValue() && `${info.getValue()}`}
        </div>
      ),
    }),
    columnHelper.accessor(
      (row) => {
        return row
      },
      {
        id: 'total_amount',
        header: () => 'Total Amount',
        cell: (info) => {
          const {
            total_amount,
            service_fee,
            service_fee_payer,
            amount_provider,
            type,
          } = info.getValue()

          const totalToBePaid = displayTotalAmountToBePaidByApplicant(
            {
              net_amount: total_amount,
              service_fee,
              serviceFeePayer: service_fee_payer,
              base_amount: amount_provider,
            },
            type
          )

          return <div>{currencyFormatter.format(totalToBePaid)}</div>
        },
      }
    ),
    columnHelper.accessor('comments', {
      id: 'comments',
      header: () => 'Comments',
      cell: (info) => (
        <div className="max-w-[5rem] truncate">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => <>{translate('status')}</>,
      cell: (info) => (
        <div>
          <TransactionStatusBadge value={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.accessor('created_datetime', {
      id: 'created_datetime',
      header: () => <>{translate('created_at')}</>,
      cell: (info) => <div>{UTCToUsersTimezone(info.getValue())}</div>,
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">{translate('action')}</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Button
            onClick={() => {
              navigate(
                AppRoutePaths.transactions.details.index.replace(
                  ':transactionId',
                  row.transaction_id.toString()
                )
              )
            }}
          >
            {translate('details')}
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: 1,
  })

  return (
    <>
      <section aria-labelledby="payments-list-title">
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              {searchParams.get('filter') === 'byStudent' && student
                ? translate('student_transactions', {
                    student_name: `${student?.first_name} ${student?.last_name}`,
                  })
                : translate('transactions')}
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {'last updated at: 06:05:32 PM'}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="table_search"
                  placeholder="Search Payments"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={false}
                    onClick={() => {}}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {'last updated at: 06:05:32 PM'}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={cn(
                          ' px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                          index != 0 &&
                            index != headerGroup.headers.length - 1 &&
                            'hidden md:table-cell',
                          index == headerGroup.headers.length - 1 && 'text-left'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {(function () {
                return (
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => (
                          <td
                            key={cell.id}
                            className={cn(
                              'py-4 px-2  overflow-x-hidden  text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                              index != 0 &&
                                index != row.getVisibleCells().length - 1 &&
                                'hidden md:table-cell',
                              index == row.getVisibleCells().length - 1 &&
                                'text-left'
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )
              })()}
            </table>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={total_rows}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}
